import React from 'react';
import MicroFrontend from '@branchjs/micro-frontend';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { Global, css } from '@emotion/react';

import { getMicroFrontendsState } from '../../selectors/MicroFrontendsSelectors';
import { isFeatureEnabled } from '../../selectors/FlipperSelectors';
import { MicroFrontendService } from '../../services/MicroFrontendService/MicroFrontendService';
import { ERROR_HANDLING } from '../../../../../shared/constants/flipperFlags';
import { MicroFrontendV2Styles } from './MicroFrontendV2.styles';

type Props = {
  name: string;
  url?: string;
  allow?: string;
  modal?: boolean;
  modalHeightOffset?: string;
  isLoading?: boolean;
};

const MODAL_HEIGHT_OFFSET = 52;

const AWS_S3_MF_BASE_URL = 'https://cdn.branch.io/micro-frontends/versions';

/**
 * This component is a HOC on original MicroFrontend that injects
 * the micro frontend URL that came from dashboard versioning service.
 *
 * Once dashboard switches to versioning service fully, it should be
 * moved to branch-micro-frontend package and replace the original
 * component.
 */
const MicroFrontendV2 = ({ name, url, allow, modal, modalHeightOffset, isLoading }: Props): JSX.Element => {
  const location = useLocation();
  const history = useHistory();

  const config = useSelector(getMicroFrontendsState);
  const isErrorHandlingEnabled: boolean = useSelector((state) => isFeatureEnabled(state, ERROR_HANDLING));
  const versioningServiceEnabled = useSelector((state) => isFeatureEnabled(state, 'versioning_service_integration'));

  const isUrlValid = (): boolean => {
    const valid: boolean = MicroFrontendService.validateUrl(url);
    if (!valid) {
      if (isErrorHandlingEnabled) {
        history.push('/out-of-service');
      }

      return false;
    } else {
      return true;
    }
  };

  const showMicroFrontend = !isErrorHandlingEnabled || isUrlValid();

  const buildFullMicroFrontendUrl = () => {
    const version = config.versions?.[name] || config.versions?.__default__;

    if (!version) {
      const camelCaseMfName = name.replace(/-([a-z])/g, (g) => g[1].toUpperCase());

      return config[camelCaseMfName]?.url;
    }

    return `${AWS_S3_MF_BASE_URL}/${version}/${name}#${location.pathname}${location.search}`;
  };

  const isVersioningServiceEnabled = () => {
    return versioningServiceEnabled && window.location.host.indexOf('localhost') <= -1;
  };

  return showMicroFrontend ? (
    <div className={isLoading ? MicroFrontendV2Styles.hiddenContainer : ''}>
      {modal && (
        <Global
          styles={css`
            #${`${name}-mf`} {
              height: calc(100vh - ${modalHeightOffset || MODAL_HEIGHT_OFFSET}px) !important;
            }
          `}
        />
      )}
      <MicroFrontend
        id={`${name}-mf`}
        url={isVersioningServiceEnabled() ? buildFullMicroFrontendUrl() : url || ''}
        allow={allow}
      />
    </div>
  ) : (
    <></>
  );
};

export default MicroFrontendV2;
